"use client";

/**
 * CAD sound effects. Everything is synthesized with the Web Audio API, so
 * there are no audio files to host and nothing to download.
 *
 * Browsers block audio until the user has interacted with the page, so call
 * `sfx.unlock()` from a click handler (the <SfxToggle /> button does this).
 * Enabled state + volume persist in localStorage.
 */

export type SfxName =
  | "newCall"
  | "panic"
  | "transmit"
  | "alertSent"
  | "hold"
  | "holdEnd"
  | "patch"
  | "error"
  | "click"
  | "message";

type Note = { f: number; d: number; at?: number; type?: OscillatorType; v?: number };

const RECIPES: Record<SfxName, Note[]> = {
  // Two rising chirps -- new incoming call
  newCall: [
    { f: 880, d: 120, v: 0.18 },
    { f: 1175, d: 160, at: 140, v: 0.18 }
  ],
  // Hi/lo siren x4 -- officer panic
  panic: Array.from({ length: 4 }, (_, i) => [
    { f: 1400, d: 190, at: i * 400, type: "square" as OscillatorType, v: 0.2 },
    { f: 900, d: 190, at: i * 400 + 200, type: "square" as OscillatorType, v: 0.2 }
  ]).flat(),
  // Tiny blip when a unit keys up somewhere
  transmit: [{ f: 1050, d: 40, v: 0.06 }],
  alertSent: [
    { f: 700, d: 90, v: 0.14 },
    { f: 1000, d: 120, at: 100, v: 0.14 }
  ],
  hold: [
    { f: 900, d: 130, v: 0.16 },
    { f: 700, d: 130, at: 150, v: 0.16 },
    { f: 500, d: 220, at: 300, v: 0.16 }
  ],
  holdEnd: [
    { f: 500, d: 110, v: 0.15 },
    { f: 800, d: 160, at: 130, v: 0.15 }
  ],
  patch: [
    { f: 600, d: 70, v: 0.12 },
    { f: 800, d: 70, at: 80, v: 0.12 }
  ],
  error: [{ f: 190, d: 180, type: "sawtooth", v: 0.1 }],
  click: [{ f: 620, d: 30, v: 0.06 }],
  // Soft two-note ping -- a player direct message arrived
  message: [
    { f: 660, d: 90, v: 0.1 },
    { f: 990, d: 140, at: 100, v: 0.1 }
  ]
};

const LS_ENABLED = "cad.sfx.enabled";
const LS_VOLUME = "cad.sfx.volume";

class Sfx {
  private ctx: AudioContext | null = null;
  private enabled = false;
  private volume = 0.7;
  private listeners = new Set<() => void>();
  private loaded = false;

  private load() {
    if (this.loaded || typeof window === "undefined") return;
    this.loaded = true;
    try {
      this.enabled = window.localStorage.getItem(LS_ENABLED) === "1";
      const v = parseFloat(window.localStorage.getItem(LS_VOLUME) ?? "");
      if (!Number.isNaN(v)) this.volume = Math.min(1, Math.max(0, v));
    } catch {
      /* storage blocked -- defaults are fine */
    }
  }

  private emit() {
    this.listeners.forEach((l) => l());
  }

  subscribe(fn: () => void) {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  getState() {
    this.load();
    return { enabled: this.enabled, volume: this.volume, unlocked: this.ctx?.state === "running" };
  }

  /** Must be called from a user gesture at least once per page load. */
  unlock() {
    if (typeof window === "undefined") return;
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    if (!this.ctx) this.ctx = new Ctx();
    if (this.ctx.state === "suspended") void this.ctx.resume();
    this.emit();
  }

  setEnabled(on: boolean) {
    this.load();
    this.enabled = on;
    try {
      window.localStorage.setItem(LS_ENABLED, on ? "1" : "0");
    } catch {}
    if (on) {
      this.unlock();
      this.play("click", true);
    }
    this.emit();
  }

  setVolume(v: number) {
    this.load();
    this.volume = Math.min(1, Math.max(0, v));
    try {
      window.localStorage.setItem(LS_VOLUME, String(this.volume));
    } catch {}
    this.emit();
  }

  play(name: SfxName, force = false) {
    this.load();
    if (!force && !this.enabled) return;
    if (!this.ctx || this.ctx.state !== "running") return; // silently skip until unlocked
    const ctx = this.ctx;
    const now = ctx.currentTime;
    for (const n of RECIPES[name]) {
      const start = now + (n.at ?? 0) / 1000;
      const end = start + n.d / 1000;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = n.type ?? "sine";
      osc.frequency.value = n.f;
      const peak = (n.v ?? 0.15) * this.volume;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(peak, start + 0.008);
      gain.gain.linearRampToValueAtTime(0, end);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(end + 0.02);
    }
  }
}

export const sfx = new Sfx();
